import React, { useState } from 'react';
import { Calendar, Home, Ruler, DollarSign, CheckCircle2, Loader2 } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { formatPhoneNumber } from '../ops/components/PhoneInput';

const EMPTY_FORM = {
  name: '',
  email: '',
  phone: '',
  address: '',
  projectType: '',
  projectSize: '',
  budget: '',
  timeline: '',
  description: ''
};

const Quote = () => {
  const { settings, loading } = useSiteSettings();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          project_type: formData.projectType,
          project_size: formData.projectSize,
          budget: formData.budget,
          timeline: formData.timeline,
          description: formData.description,
        }),
      });
      if (!res.ok) throw new Error('failed');
      setSubmitted(true);
      setFormData(EMPTY_FORM);
    } catch {
      setError('Something went wrong sending your request. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (loading || !settings) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-[#1A1A1A]">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Get Your Free <span className="text-[#D4AF37]">Quote</span>
          </h1>
          <div className="w-20 h-1 bg-[#D4AF37] mx-auto mb-6"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Tell us about your project and we'll provide a detailed estimate tailored to your needs
          </p>
        </div>
      </section>

      {/* Quote Form */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Why Choose Us Cards */}
            <div className="space-y-6">
              <Card className="bg-[#1A1A1A] border-gray-800 p-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-[#D4AF37] p-3 rounded-lg">
                    <Home className="text-black" size={24} />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-2">Expert Guidance</h3>
                    <p className="text-gray-400 text-sm">
                      Our team will help you understand every aspect of your project
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="bg-[#1A1A1A] border-gray-800 p-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-[#D4AF37] p-3 rounded-lg">
                    <DollarSign className="text-black" size={24} />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-2">Transparent Pricing</h3>
                    <p className="text-gray-400 text-sm">
                      No hidden fees. Clear, itemized quotes for your budget planning
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="bg-[#1A1A1A] border-gray-800 p-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-[#D4AF37] p-3 rounded-lg">
                    <Calendar className="text-black" size={24} />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-2">Flexible Timeline</h3>
                    <p className="text-gray-400 text-sm">
                      We work around your schedule to minimize disruption
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="bg-[#1A1A1A] border-gray-800 p-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-[#D4AF37] p-3 rounded-lg">
                    <Ruler className="text-black" size={24} />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-2">Custom Solutions</h3>
                    <p className="text-gray-400 text-sm">
                      Every project is unique and deserves a tailored approach
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Quote Form */}
            <div className="lg:col-span-2">
              {submitted ? (
                <Card className="bg-[#1A1A1A] border-[#D4AF37]/50 p-10 text-center" data-testid="quote-request-success">
                  <CheckCircle2 size={52} className="text-[#D4AF37] mx-auto mb-5" />
                  <h2 className="text-3xl font-bold text-white mb-3">Request <span className="text-[#D4AF37]">Received!</span></h2>
                  <p className="text-gray-300 max-w-lg mx-auto">
                    Thank you for reaching out. Our team has received your quote request and will be in touch shortly to discuss your project.
                  </p>
                  <Button onClick={() => setSubmitted(false)} variant="outline"
                    className="mt-8 border-[#D4AF37]/50 text-[#D4AF37] hover:text-[#B8941F]" data-testid="submit-another-btn">
                    Submit Another Request
                  </Button>
                </Card>
              ) : (
              <Card className="bg-[#1A1A1A] border-gray-800 p-8">
                <h2 className="text-3xl font-bold text-white mb-6">
                  Project <span className="text-[#D4AF37]">Details</span>
                </h2>
                <div className="w-20 h-1 bg-[#D4AF37] mb-8"></div>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Information */}
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-4">Contact Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="text-white mb-2 block font-medium">Full Name</label>
                        <Input
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Doe"
                          required
                          className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                        />
                      </div>
                      <div>
                        <label className="text-white mb-2 block font-medium">Email</label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@example.com"
                          required
                          className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                      <div>
                        <label className="text-white mb-2 block font-medium">Phone</label>
                        <Input
                          name="phone"
                          value={formData.phone}
                          onChange={(e) => handleChange({ target: { name: 'phone', value: formatPhoneNumber(e.target.value) } })}
                          placeholder="330-316-7136"
                          required
                          className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                        />
                      </div>
                      <div>
                        <label className="text-white mb-2 block font-medium">Project Address</label>
                        <Input
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          placeholder="123 Main St, Cleveland, OH"
                          required
                          className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Project Details */}
                  <div className="border-t border-gray-700 pt-6">
                    <h3 className="text-xl font-semibold text-white mb-4">Project Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="text-white mb-2 block font-medium">Project Type</label>
                        <select
                          name="projectType"
                          value={formData.projectType}
                          onChange={handleChange}
                          required
                          className="w-full bg-[#2A2A2A] border border-gray-700 text-white rounded-md px-3 py-2 focus:border-[#D4AF37] focus:outline-none"
                        >
                          <option value="">Select project type</option>
                          <option value="kitchen-remodel">Kitchen Remodeling</option>
                          <option value="bathroom-remodel">Bathroom Renovation</option>
                          <option value="addition">Home Addition</option>
                          <option value="basement">Basement Finishing</option>
                          <option value="roofing-siding">Roofing & Siding</option>
                          <option value="interior">Interior Remodeling</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-white mb-2 block font-medium">Project Size (sq ft)</label>
                        <Input
                          name="projectSize"
                          value={formData.projectSize}
                          onChange={handleChange}
                          placeholder="e.g., 2500"
                          required
                          className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                      <div>
                        <label className="text-white mb-2 block font-medium">Estimated Budget</label>
                        <select
                          name="budget"
                          value={formData.budget}
                          onChange={handleChange}
                          required
                          className="w-full bg-[#2A2A2A] border border-gray-700 text-white rounded-md px-3 py-2 focus:border-[#D4AF37] focus:outline-none"
                        >
                          <option value="">Select budget range</option>
                          <option value="under-100k">Under $100,000</option>
                          <option value="100k-250k">$100,000 - $250,000</option>
                          <option value="250k-500k">$250,000 - $500,000</option>
                          <option value="500k-1m">$500,000 - $1,000,000</option>
                          <option value="over-1m">Over $1,000,000</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-white mb-2 block font-medium">Desired Timeline</label>
                        <select
                          name="timeline"
                          value={formData.timeline}
                          onChange={handleChange}
                          required
                          className="w-full bg-[#2A2A2A] border border-gray-700 text-white rounded-md px-3 py-2 focus:border-[#D4AF37] focus:outline-none"
                        >
                          <option value="">Select timeline</option>
                          <option value="asap">As soon as possible</option>
                          <option value="1-3months">1-3 months</option>
                          <option value="3-6months">3-6 months</option>
                          <option value="6-12months">6-12 months</option>
                          <option value="flexible">Flexible</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Project Description */}
                  <div className="border-t border-gray-700 pt-6">
                    <label className="text-white mb-2 block font-medium">Project Description</label>
                    <Textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Please provide details about your project, including any specific requirements, features, or concerns..."
                      rows={6}
                      required
                      className="bg-[#2A2A2A] border-gray-700 text-white focus:border-[#D4AF37]"
                    />
                  </div>

                  {error && <p className="text-red-400 text-sm" data-testid="quote-request-error">{error}</p>}
                  <Button 
                    type="submit" 
                    disabled={submitting}
                    data-testid="submit-quote-request-btn"
                    className="w-full bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold text-lg py-6"
                  >
                    {submitting && <Loader2 className="animate-spin mr-2" size={18} />}
                    {submitting ? 'Sending...' : 'Submit Quote Request'}
                  </Button>
                </form>
              </Card>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Quote;
